import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { AlertBanner } from '@/components/ui/alert-banner';
import { formatCurrency } from '@/lib/utils';
import type { BasketChange, UsageWarning } from '@/lib/addon-basket';

export interface SummaryUsageRow {
  /** server limit key, e.g. 'maxUsers' */
  limitKey: string;
  used: number;
}

export interface AddonSummaryProps {
  currency?: string;
  /** monthly bill now (server) */
  currentMonthly: number | null | undefined;
  /** monthly bill with the draft (server preview) */
  newMonthly: number | null | undefined;
  /** the preview is being (re)calculated */
  calculating?: boolean;
  previewError?: boolean;
  changes: BasketChange[];
  nameOf: (addonId: string) => string;
  /** limits now / after the draft (server) */
  currentLimits?: Record<string, number | null | undefined> | null;
  newLimits?: Record<string, number | null | undefined> | null;
  usageRows: SummaryUsageRow[];
  warnings: UsageWarning[];
  /** limits are unlimited while the business is on a trial */
  trialAccess?: boolean;
  /** 'add': changes are requests on top of what exists, so "remove" never appears */
  children?: ReactNode;
}

function money(v: number | null | undefined, currency?: string): string {
  return v === null || v === undefined ? '—' : formatCurrency(v, currency);
}

export function AddonSummary(p: AddonSummaryProps) {
  const { t } = useTranslation('settings');
  const diff = p.currentMonthly !== null && p.currentMonthly !== undefined && p.newMonthly !== null && p.newMonthly !== undefined ? p.newMonthly - p.currentMonthly : null;
  const limitText = (v: number | null | undefined) => (v === null ? t('addonNoLimit') : v === undefined ? '—' : v.toLocaleString());

  return (
    <div className="space-y-4 rounded-xl border bg-card p-4" data-testid="addon-summary">
      <h3 className="text-sm font-semibold text-foreground">{t('addonSummaryTitle')}</h3>

      <dl className="space-y-1.5 text-sm">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-muted-foreground">{t('summaryCurrent')}</dt>
          <dd className="font-medium" data-testid="summary-current"><bdi dir="ltr">{money(p.currentMonthly, p.currency)}</bdi></dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="font-semibold text-foreground">{t('summaryNew')}</dt>
          <dd className="flex items-center gap-1.5 text-lg font-bold text-primary-text" data-testid="summary-new" aria-live="polite">
            {p.calculating && <Loader2 className="h-4 w-4 animate-spin" aria-label={t('summaryCalculating')} />}
            <bdi dir="ltr">{money(p.newMonthly, p.currency)}</bdi>
          </dd>
        </div>
        {diff !== null && diff !== 0 && (
          <div className="flex items-baseline justify-between gap-3 text-xs">
            <dt className="text-muted-foreground">{t('summaryDiff')}</dt>
            <dd className={diff > 0 ? 'font-medium text-warning-text' : 'font-medium text-success-text'}>
              <bdi dir="ltr">{diff > 0 ? '+' : '−'}{formatCurrency(Math.abs(diff), p.currency)}</bdi>
            </dd>
          </div>
        )}
      </dl>
      {p.previewError && <AlertBanner tone="danger" className="mb-0">{t('summaryPreviewError')}</AlertBanner>}

      <div className="space-y-1.5 border-t pt-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('summaryChanges')}</p>
        {p.changes.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('summaryNoChanges')}</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {p.changes.map((c) => (
              <li key={c.addonId} className="flex items-baseline justify-between gap-2">
                <span className="min-w-0 truncate" title={p.nameOf(c.addonId)}>
                  {c.kind === 'add' ? t('changeAdd', { name: p.nameOf(c.addonId) }) : c.kind === 'remove' ? t('changeRemove', { name: p.nameOf(c.addonId) }) : p.nameOf(c.addonId)}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  <bdi dir="ltr">{c.kind === 'add' ? `×${c.to}` : c.kind === 'remove' ? `×${c.from}` : `${c.from} → ${c.to}`}</bdi>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-1.5 border-t pt-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('summaryLimits')}</p>
        {p.trialAccess && <p className="text-xs text-muted-foreground">{t('summaryTrialNote')}</p>}
        <ul className="space-y-1 text-sm">
          {p.usageRows.map((r) => {
            const now = p.currentLimits?.[r.limitKey];
            const next = p.newLimits?.[r.limitKey];
            const over = p.warnings.some((w) => w.limitKey === r.limitKey);
            return (
              <li key={r.limitKey} className={over ? 'text-destructive-text' : undefined}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 truncate">{t(`addonLimitName_${r.limitKey}`)}</span>
                  <span className="shrink-0 text-xs"><bdi dir="ltr">{t('summaryUsedOf', { used: r.used, max: limitText(next) })}</bdi></span>
                </div>
                {next !== now && now !== undefined && next !== undefined && (
                  <p className="text-xs text-muted-foreground"><bdi dir="ltr">{t('addonLimitFromTo', { from: limitText(now), to: limitText(next) })}</bdi></p>
                )}
              </li>
            );
          })}
        </ul>
        {p.warnings.length > 0 && (
          <AlertBanner tone="warning" icon={AlertTriangle} className="mb-0 mt-2">
            <p className="font-medium">{t('summaryWarnTitle')}</p>
            <ul className="mt-1 list-disc ps-4 text-xs">
              {p.warnings.map((w) => (
                <li key={w.limitKey}>{t('summaryWarnLimit', { name: t(`addonLimitName_${w.limitKey}`), used: w.used, max: w.max })}</li>
              ))}
            </ul>
          </AlertBanner>
        )}
      </div>

      {p.children}
    </div>
  );
}
